// ImageWithLoader component for showing a spinner while loading images
interface ImageWithLoaderProps {
  src: string;
  alt?: string;
  className?: string;
}

const ImageWithLoader: React.FC<ImageWithLoaderProps> = ({
  src,
  alt,
  className,
}) => {
  const [loading, setLoading] = React.useState(true);
  return (
    <div className="relative w-full flex justify-center items-center min-h-[8rem]">
      {loading && (
        <span className="absolute z-10 flex items-center justify-center w-full h-full">
          <svg
            className="animate-spin h-8 w-8 text-primary"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
        </span>
      )}
      <img
        src={src}
        alt={alt}
        className={className}
        style={loading ? { visibility: "hidden" } : {}}
        onLoad={() => setLoading(false)}
        onError={() => setLoading(false)}
      />
    </div>
  );
};
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Format product name to render ® and ™ as superscript
function formatProductName(name: string): string {
  if (!name) return "";
  return name.replace(/(®|™)/g, "<sup>$1</sup>");
}

type CheckboxItemProps = {
  label: string;
  checked: boolean;
  onToggle: () => void;
};

const CheckboxItem: React.FC<CheckboxItemProps> = ({
  label,
  checked,
  onToggle,
}) => {
  return (
    <label className="flex items-center gap-2 cursor-pointer text-gray-700 select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="sr-only"
      />
      <span
        className={`w-5 h-5 rounded-sm border flex items-center justify-center transition-colors
          ${
            checked ? "bg-primary border-primary" : "border-gray-300 bg-white"
          }`}
        aria-hidden="true"
      >
        {checked && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-3 h-3"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 13l4 4L19 7" />
          </svg>
        )}
      </span>
      <span>{label}</span>
    </label>
  );
};

const ProductDetails: React.FC = () => {
  const [filters, setFilters] = useState<{
    brands: string[];
    categories: string[];
    forms: string[];
  }>({ brands: [], categories: [], forms: [] });
  const location = useLocation();
 const navigate = useNavigate();
  const [products, setProducts] = useState<any[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Modal state
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);

  // Synchronize category and search query params with state
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    // Category sync
    const categoryParam = searchParams.get("category");
    if (categoryParam) {
      const categories = categoryParam
        .split(",")
        .map((c) => c.trim())
        .filter(Boolean);
      setSelectedFilters(categories);
    } else {
      setSelectedFilters([]);
    }

    // Search sync
    const searchParam = searchParams.get("search");
    if (searchParam) {
      setSearchQuery(searchParam);
    } else {
      setSearchQuery("");
    }
  }, [location.search]);



  const images = [
    selectedProduct?.image,
    selectedProduct?.image2,
    selectedProduct?.image3,
    selectedProduct?.image4,
    selectedProduct?.image5,
    selectedProduct?.image6,
    selectedProduct?.image7,
  ].filter(Boolean); // remove undefined/null

  useEffect(() => {
    if (images.length <= 1) return; // 🚫 no slide if only one image

    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % images.length);
    }, 3000); // change every 3s

    return () => clearInterval(interval);
  }, [images]);

  useEffect(() => {
    if (selectedProduct) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedProduct]);

  const formatTrademark = (text: string): string => {
    if (!text) return "";
    return (
      text
        // Add italics for "Vibrio" (case-insensitive)
        .replace(/\b(Vibrio)\b/gi, "<i>$1</i>")
        // Handle ® and ™ symbols
        .replace(/®/g, "<sup>®</sup>")
        .replace(/™/g, "<sup>™</sup>")
        .replace(/\(R\)/gi, "<sup>®</sup>")
        .replace(/\(TM\)/gi, "<sup>™</sup>")
    );
  };

  // Fetch JSON data
  useEffect(() => {
    fetch("/data/products.json")
      .then((res) => res.json())
      .then((data) => {
        setFilters({
          brands: data.brands,
          categories: data.categories,
          forms: data.forms,
        });
        setProducts(data.products);
      })
      .catch((err) => console.error("Error loading JSON:", err));
  }, []);

  const toggleFilter = (filter: string) => {
    const nextFilters = selectedFilters.includes(filter)
      ? selectedFilters.filter((f) => f !== filter)
      : [...selectedFilters, filter];

    const newParams = new URLSearchParams(location.search);
    if (nextFilters.length > 0) {
      newParams.set("category", nextFilters.join(","));
    } else {
      newParams.delete("category");
    }
    navigate({ search: newParams.toString() }, { replace: true });
  };

  const removeFilter = (filter: string) => {
    const nextFilters = selectedFilters.filter((f) => f !== filter);
    const newParams = new URLSearchParams(location.search);
    if (nextFilters.length > 0) {
      newParams.set("category", nextFilters.join(","));
    } else {
      newParams.delete("category");
    }
    navigate({ search: newParams.toString() }, { replace: true });
  };

  const resetFilters = () => {
    const newParams = new URLSearchParams(location.search);
    newParams.delete("category");
    newParams.delete("search");
    navigate({ search: newParams.toString() }, { replace: true });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    const newParams = new URLSearchParams(location.search);
    if (value) {
      newParams.set("search", value);
    } else {
      newParams.delete("search");
    }
    navigate({ search: newParams.toString() }, { replace: true });
  };

  const isChecked = (v: string) => selectedFilters.includes(v);

  const filteredProducts = products
    .filter((p) => {
      if (p.nameId === "rapigrow-me") return false;

      const matchesSearch =
        searchQuery === "" ||
        (p.name && p.name.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesFilter =
        selectedFilters.length === 0 ||
        selectedFilters.includes(p.brand) ||
        selectedFilters.includes(p.category) ||
        selectedFilters.includes(p.form);

      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      const order = [
        "BIOFENCE™ STAGE-I",
        "BIOFENCE™ STAGE-II",
        "BIOFENCE™ STAGE-III",
      ];

      const nameA = a.name || "";
      const nameB = b.name || "";

      if (a.brand === "Poultry" && b.brand === "Poultry") {
        const indexA = order.indexOf(nameA);
        const indexB = order.indexOf(nameB);

        // If both are BIOFENCE products, follow special order
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;

        // If one is BIOFENCE and other is not, still keep alphabetical
        if (indexA !== -1 || indexB !== -1) {
          if (nameA === nameB) return 0;
          return nameA.localeCompare(nameB);
        }
      }

      // Default alphabetical for all
      return nameA.localeCompare(nameB);
    });

  return (
    <section className="bg-gray-50 pt-5 pb-20 min-h-screen" id="courseSection">
      <div className="container mx-auto px-4">
        {/* Active Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-5 min-h-[50px] lg:justify-center">
          {selectedFilters.map((filter, i) => (
            <button
              key={i}
              className="px-4 py-1.5 text-sm font-medium border rounded-full border-gray-300 text-gray-700 flex items-center gap-2 bg-white shadow hover:bg-gray-100 cursor-pointer"
              onClick={() => removeFilter(filter)}
            >
              <span>{filter}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-gray-500 hover:text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-4 ">
          {/* Sidebar */}
          <aside className="w-full lg:w-1/4 bg-white border border-gray-200 p-5 rounded-lg shadow-sm lg:sticky lg:top-24 h-fit">
            {/* Brand */}
            <div className="mb-6">
              <h5 className="text-gray-900 font-semibold mb-3">
                Animal Category
              </h5>
              <div className="space-y-2">
                {filters.brands.map((brand) => (
                  <CheckboxItem
                    key={brand}
                    label={brand}
                    checked={isChecked(brand)}
                    onToggle={() => toggleFilter(brand)}
                  />
                ))}
              </div>
            </div>

            {/* Category */}
            {/* <div className="mb-6">
              <h5 className="text-gray-900 font-semibold mb-3">Category</h5>
              <div className="space-y-2">
                {filters.categories.map((cat) => (
                  <CheckboxItem
                    key={cat}
                    label={cat}
                    checked={isChecked(cat)}
                    onToggle={() => toggleFilter(cat)}
                  />
                ))}
              </div>
            </div> */}

            {/* Form */}
            {/* <div className="mb-6">
              <h5 className="text-gray-900 font-semibold mb-3">Form</h5>
              <div className="space-y-2">
                {filters.forms.map((form) => (
                  <CheckboxItem
                    key={form}
                    label={form}
                    checked={isChecked(form)}
                    onToggle={() => toggleFilter(form)}
                  />
                ))}
              </div>
            </div> */}

            {/* Reset Button */}
            <div className="pt-4">
              <button
                onClick={resetFilters}
                className="w-full cursor-pointer  text-white px-6 py-2 rounded-full font-semibold hover:opacity-90"
                style={{
                  background:
                    "linear-gradient(270.95deg, #136A85 0%, #00A3D4 87.52%)",
                }}
              >
                Reset All
              </button>
            </div>
          </aside>

          {/* Main Content */}
          <div className="w-full lg:w-3/4">
            {/* Search */}
            <div className="flex mb-4">
              <input
                type="text"
                placeholder="Search by product name"
                className="flex-1 bg-white border border-gray-300 px-3 py-2 rounded-l-md focus:outline-none focus:border-gray-400"
                id="productNameInput"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <button
                type="button"
                id="courseSearch"
                className="text-white px-4 py-2 rounded-r-md flex items-center justify-center cursor-pointer"
                style={{
                  background:
                    "linear-gradient(270.95deg, #136A85 0%, #00A3D4 87.52%)",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1110.5 3a7.5 7.5 0 016.15 13.65z"
                  />
                </svg>
              </button>
            </div>

            {/* Product Grid */}
            <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200 mb-20">
              <div
                id="productsCardsContainer"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {filteredProducts.map((product, i) => (
                  <div
                    key={i}
                    onClick={() => navigate(`/our-products/${product.nameId}`)}
                    className="group border border-gray-200 bg-white rounded-lg shadow-sm hover:shadow-xl hover:-translate-y-1.5 hover:border-[#ea4b20]/60 transition-all duration-500 cursor-pointer flex flex-col h-full"
                  >
                    {/* Image wrapper */}
                    <div className="w-full aspect-[4/3] bg-white flex items-center justify-center overflow-hidden rounded-t-lg">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="object-contain w-full h-full p-4 transition-transform duration-500 group-hover:scale-108"
                      />
                    </div>

                    {/* Content */}
                    <div className="p-4 text-sm bg-blue-50 text-gray-900 group-hover:text-white group-hover:bg-[#E85222] transition-colors duration-300 mt-auto">
                      <p className="mb-1">
                        <span className="font-semibold">Product Name:</span>{" "}
                        <span
                          dangerouslySetInnerHTML={{
                            __html: formatProductName(product.name),
                          }}
                        />
                      </p>

                      <p className="mb-1">
                        <span className="font-semibold">Animal:</span>{" "}
                        {product.brand}
                      </p>
                    </div>
                  </div>
                ))}

                {filteredProducts.length === 0 && (
                  <p className="text-gray-500 col-span-full text-center py-5">
                    No products found.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50">
          <div className="bg-white w-11/12 lg:w-3/4 xl:w-2/3 max-h-[90vh] overflow-y-auto rounded-lg shadow-lg relative">
            {/* Sticky Header: Logo + Close */}
            <div className="sticky top-0 bg-white flex items-center justify-between p-3 z-10 border-b">
              {/* Logo */}
              {selectedProduct.imglogo && (
                <img
                  src={selectedProduct.imglogo}
                  alt={selectedProduct.name}
                  loading="lazy"
                  className={`object-contain 
              ${
                ["Snail", "Ruminant"].includes(selectedProduct.brand)
                  ? "h-25"
                  : "h-20"
              }
            `}
                />
              )}

              {/* Close Button */}
              <button
                onClick={() => setSelectedProduct(null)}
                className="text-gray-500 hover:text-gray-800 text-4xl cursor-pointer"
              >
                &times;
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="p-6 grid grid-cols-1 lg:grid-cols-10 gap-6">
              {/* Left Content */}
              <div className="lg:col-span-7 space-y-4">
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Animal Category :</span>{" "}
                  {selectedProduct.brand}
                </p>

                {/* Description */}
                {/* <p className="text-gray-700 font-bold text-justify">
            {selectedProduct.description?.replace(/_x000D_/g, "").trim()}
          </p> */}

                {selectedProduct.id === 16 && (
                  <span className="text-gray-700 font-semibold mb-3 block">
                    Key Ingredients:{" "}
                    <span className="font-normal">
                      {selectedProduct.key_ingredients}
                    </span>
                  </span>
                )}

                {/* Benefits */}
                <div
                  className="bg-blue-50 p-4 rounded-lg text-justify"
                  style={{
                    background:
                      "linear-gradient(309.45deg, rgba(0, 167, 89, 0.08) 24.42%, rgba(0, 163, 212, 0.08) 69.56%)",
                  }}
                >
                  <h6 className="font-semibold mb-2">Benefits</h6>
                  <ul className="list-disc pl-5 space-y-1">
                    {selectedProduct.benefits &&
                      selectedProduct.benefits
                        .flatMap((b: string) => b.split("_x000D_\n"))
                        .map((line: string, i: number) => (
                          <li
                            key={i}
                            className="text-gray-700"
                            dangerouslySetInnerHTML={{
                              __html: formatTrademark(line.trim()),
                            }}
                          />
                        ))}
                  </ul>
                </div>

                <div>
                  <h6 className="font-semibold text-justify">
                    Dosage & Usage :
                  </h6>
                  <p
                    className="mt-5 text-gray-700"
                    dangerouslySetInnerHTML={{
                      __html: formatTrademark(
                        selectedProduct.useage_dosage
                          // Replace the line break pattern with <br />
                          ?.replace(/_x000D_\n?/g, "<br />") || ""
                      ),
                    }}
                  />
                </div>

                <div>
                  <h6 className="font-semibold text-justify">Presentation:</h6>
                  <p
                    className="mt-4 text-gray-700"
                    dangerouslySetInnerHTML={{
                      __html: formatTrademark(
                        selectedProduct.presentation || ""
                      ),
                    }}
                  />
                </div>

                {selectedProduct.storage &&
                  selectedProduct.storage !== selectedProduct.dosage && (
                    <div>
                      <h6 className="font-semibold">Storage:</h6>
                      <p
                        className="text-gray-700"
                        dangerouslySetInnerHTML={{
                          __html: formatTrademark(
                            selectedProduct.storage || ""
                          ),
                        }}
                      />
                    </div>
                  )}
              </div>

              {/* Right Image / Carousel */}
              <div className="lg:col-span-3 flex flex-col justify-start mt-10 items-center w-full">
                {images.length > 0 && (
                  <div className="relative w-full flex flex-col items-center">
                    {/* Image Slider with Loading Spinner */}
                    <div className="overflow-hidden w-full flex justify-center">
                      <ImageWithLoader
                        src={images[carouselIndex]}
                        alt={selectedProduct?.name}
                        className="max-h-80 object-contain transition-all duration-700 ease-in-out"
                      />
                    </div>

                    {/* Dots Indicator */}
                    {images.length > 1 && (
                      <div className="flex justify-center mt-3 space-x-2">
                        {images.map((_, idx) => (
                          <span
                            key={idx}
                            onClick={() => setCarouselIndex(idx)}
                            className={`block cursor-pointer w-[15px] h-[15px] rounded-full ${
                              carouselIndex === idx
                                ? "bg-primary"
                                : "bg-gray-400"
                            }`}
                          ></span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProductDetails;
